import * as React from 'react';
import { connect } from 'react-redux';
import { Switch, Redirect } from 'react-router';
import { NavLink } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { Popup } from 'semantic-ui-react';
import NetworkForms from '@modules/forms/components/network-forms/network-forms';
import NetworkComponentPermission from '@common/components/permission/network-component-permission';
import EmployeeAcademy from '@modules/learning/components/employee-academy/employee-academy';
import { DialogWrapper } from '@common/components/modal/dialog-wrapper';
import Card from '@common/components/card';
import RecognitionCard from '@common/components/recognition-card/recognition-card';
import { isEmployeeOnboardingEnabled } from '@modules/core/selectors/logged-user-access';
import AccessRequestsModal from '@modules/social/components/access-requests-widget/access-requests-modal';
import * as AnalyticsService from '../../common/services/analytics';
import Route, { PermissionRoute, loadable } from '../../common/components/route';
import Permission from '../../common/components/permission';
import { TopBarContentContext } from './top-bar-content-context';
import Icon from '../../common/components/icon';
import ErrorComponent from '../../common/components/error';
import RouteNotFound from '../../common/components/route-not-found';
import TopBar from '../../common/components/top-bar';
import NavigationBar from '../../common/components/navigation-bar';
import Notifications from '../core/components/notifications';
import ProfileContainer from '../core/containers/profile';
import { UserProfileTypes } from '../core/definitions';
import * as organisationSelector from '../organisation/selectors/organisation';
import * as networkSelector from './selectors/network';
import * as userSelector from '../core/selectors/logged-user';
import {
  ESourceTypes, EComponentTypes, EPermissions, ENotificationActivityTypes
} from '../../common/definitions';
import SurveySubmissionEditor from './containers/surveys/survey-submission-editor';
import ProfileImage from '../../common/components/profile-image';
import { AccessRequestsProvider, useAccessRequestsContext } from '../social/contexts/access-requests-context';


const { EEventNames } = AnalyticsService;

require('./styles.scss');

// Component to render the AccessRequestsModal using context
const AccessRequestsModalRenderer = () => {
  const { isModalOpen, closeModal } = useAccessRequestsContext();

  return isModalOpen ? (
    <AccessRequestsModal open={isModalOpen} onClose={closeModal} />
  ) : null;
};


const ChatContainer = loadable(() => import('../chat/containers/chat'));
const MessageContainer = loadable(() => import('../social/containers/message-detail'));
const SocialContainer = loadable(() => import('../social/containers/social'));
const CalendarContainer = loadable(() => import('../calendar/container/calendar'));
const AnalyticsContainer = loadable(() => import('../analytics'));
const NetworkUserContainer = loadable(() => import('./containers/user'));
const EmployeesContainer = loadable(() => import('./containers/users'));
const SettingsContainer = loadable(() => import('./containers/settings'));
const EventsRouter = loadable(() => import('../events/network-router'));
const DocumentsContainer = loadable(() => import('../documents/containers/documents-container'));
const HighfivesContainer = loadable(() => import('../admin/containers/highfive'));
const PerformanceContainer = loadable(() => import('../performance/template'));

const isFormsActive = (match, location) => {
  const dirs = location.pathname.split('/');
  return (dirs[1] === 'networks') && (dirs[3] === 'forms');
};

class NetworkTemplate extends React.Component {

  static getDerivedStateFromError(error) {
    return { error };
  }

  constructor() {
    super();

    this.state = {
      error: null,
      showRecognitionModal: false,
      recognition: null,
      isNotificationOpen: false
    };

    this.setTopBarContentRef = (ref) => (this.topBarContentRef = ref);
    this.handleNotificationClick = this.handleNotificationClick.bind(this);
    this.handleLogout = this.handleLogout.bind(this);
    this.closeRecognitionModal = this.closeRecognitionModal.bind(this);
    this.toggleNotification = this.toggleNotification.bind(this);
  }

  UNSAFE_componentWillMount() {
    AnalyticsService.register({ networkId: this.props.network.id });
  }

  UNSAFE_componentWillUpdate(nextProps) {
    if (this.props.location.pathname !== nextProps.location.pathname && this.state.error) {
      this.state.error = null;
    }
  }

  componentWillUnmount() {
    AnalyticsService.register({ networkId: undefined });
  }

  static props;

  handleNotificationClick(notification) {
    const {
      history, network, selectNetwork, location
    } = this.props;
    const { id, source_type: sourceType, source_id: sourceId } = notification;

    const fnWrapper = (fn) => () => {
      AnalyticsService.track(EEventNames.OPENED_NOTIFICATION, {
        activityType: notification.activity_type,
        sourceType,
        sourceId,
      });

      fn();
    };

    switch (sourceType) {
      case ESourceTypes.SURVEY:
        return fnWrapper(() => {
          const { pathname, search } = location;
          const origin = encodeURIComponent(`${pathname}${search}`);
          const url = `/networks/${network.id}/survey/${sourceId}/1?origin=${origin}`;
          return history.push(url);
        });
      case ESourceTypes.EXCHANGE:
        if (!notification.network_id) return;

        return fnWrapper(async () => {
          await selectNetwork(notification.network_id);

          return history.push(`/networks/${notification.network_id}/flexchange/exchanges/${sourceId}`);
        });
      case ESourceTypes.MESSAGE:
        return fnWrapper(() => history.push(`/messages/${sourceId}?aid=${id}`));
      case ESourceTypes.CONVERSATION:
        return fnWrapper(() => {
          return history.push(`/conversations/${sourceId}`);
        });
      case ESourceTypes.EVENT:
        return fnWrapper(() => history.push(`/networks/${network.id}/events/${notification.source_id}`));
      case ESourceTypes.FORM_SUBMISSION:
        const isCommentCreated = (
          notification.activity_type ===
          ENotificationActivityTypes.FORM_SUBMISSION_COMMENT_CREATED
        );
        if (
          notification.activity_type !== ENotificationActivityTypes.FORM_SUBMISSION_CREATED &&
          !isCommentCreated
        ) {
          return;
        }
        const formId = notification?.meta?.form?.id;
        const subId = notification?.source_id;
        if (!formId || !subId) return;
        let path = `/networks/${network.id}/forms/${formId}/submissions/${subId}`;
        if (isCommentCreated) {
          path += '?showComments=1';
        }
        return fnWrapper(() => history.push(path));
      case ESourceTypes.RECOGNITION:
        return fnWrapper(() => {
          this.setState({
            showRecognitionModal: true,
            recognition: notification,
            isNotificationOpen: false
          });
        });
      case ESourceTypes.COURSE:
        return fnWrapper(() => {
          if (
            notification.activity_type === ENotificationActivityTypes.NEW_COURSE ||
            notification.activity_type === ENotificationActivityTypes.COURSE_ALMOST_DUE
          ) {
            const url = `/networks/${network.id}/academy/course/${notification.source_id}`;
            return history.push(url);
          }
          return `/networks/${network.id}/academy`;
        });
      case ESourceTypes.CHANNEL_ACCESS_REQUEST:
        return fnWrapper(() => {
          this.setState({ isNotificationOpen: false });
          // Open the access requests modal directly instead of redirecting
          if (this.props.openAccessRequestsModal) {
            this.props.openAccessRequestsModal(notification.meta.channel.id);
          }
        });
      case ESourceTypes.CHANNEL:
        return fnWrapper(() => {
          return history.push(`/networks/${network.id}/messages/channel/${notification.meta.channel.id}`);
        });
      case ESourceTypes.PERFORMANCE_EVALUATION:
        return fnWrapper(() => {
          return history.push(`/networks/${network.id}/performance/my-evaluations/${notification.source_id}`);
        });
      default:
    }
  }

  handleLogout() {
    const { logout, history } = this.props;

    logout();
    history.push('/auth/login');
  }

  closeRecognitionModal() {
    this.setState({
      showRecognitionModal: false,
      recognition: null
    });
  }

  toggleNotification(isOpen) {
    this.setState({ isNotificationOpen: isOpen });
    if (!isOpen) {
      this.props.clearNotifications();
    }
  }

  render() {
    const { error, showRecognitionModal, recognition } = this.state;
    const {
      history, loggedUser, organisation, networks, network, unread, t,
      onboardingEnabled, elearningEnabled
    } = this.props;

    const networkUrl = (path) => `/networks/${network.id}${path}`;

    const showAcademy = (
      (onboardingEnabled || elearningEnabled) &&
      !!process.env.FLAG_ACADEMY
    );
    const academyUrl = networkUrl('/academy');

    return (
      <div id="NetworkTemplate">
        <NavigationBar organisation={organisation} onHome={() => history.push(networkUrl('/feed'))}>
          <Permission component={EComponentTypes.SOCIAL}>
            <NavigationBar.Item
              id="feed"
              path={networkUrl('/feed')}
              isActive={(match, l) => (
                l.pathname.includes('/networks/') &&
                (l.pathname.includes('/feed') ||
                  l.pathname.includes('/messages'))
              )}
              icon="home"
              tooltip={t('core:tab_timeline')}
              unread={unread.social.total}
            />
          </Permission>
          <NavigationBar.Item
            id="colleagues" path={networkUrl('/users')} icon="group"
            tooltip={t('core:tab_coworkers')}
          />
          {
            showAcademy && (
              <NavigationBar.Item
                id="learning" path={academyUrl}
                icon="school" tooltip={t('core:tab_elearning')}
              />
            )
          }
          <Permission component={EComponentTypes.FORMS}>
            <NavigationBar.Item
              id="forms" path={networkUrl('/forms/me/forms')} icon="assignment"
              tooltip={t('core:tab_forms')}
              isActive={isFormsActive}
            />
          </Permission>
          <Permission component={EComponentTypes.EVENTS}>
            <NavigationBar.Item
              id="events" path={networkUrl('/events')}
              icon="event" tooltip={t('core:tab_events')}
            />
          </Permission>
          <Permission component={EComponentTypes.DOCUMENTS}>
            <NavigationBar.Item
              id="documents" path={networkUrl('/documents')}
              icon="folder" tooltip={t('core:tab_documents')}
            />
          </Permission>
          <Permission component={EComponentTypes.CALENDAR}>
            <NavigationBar.Item
              id="calendar" path={networkUrl('/calendar')}
              icon="calendar_today" tooltip={t('core:tab_calendar')}
            />
          </Permission>
          <Permission name={[
            EPermissions.NETWORK_USERS_VIEW,
          ]}
          >
            <NavigationBar.Item
              id="highfives" path={networkUrl('/highfives')} icon="highfive"
              tooltip={t('core:tab_highfives')}
            />
          </Permission>
          <Permission name={[
            EPermissions.NETWORK_USER_STATISTICS_VIEW,
            EPermissions.NETWORK_COMMUNICATION_STATISTICS_VIEW,
            EPermissions.NETWORK_EXCHANGE_STATISTICS_VIEW,
            EPermissions.NETWORK_ONBOARDING_STATISTICS
          ]}
          >
            <NavigationBar.Item
              id="analytics" path={networkUrl('/analytics')} icon="analytics"
              tooltip={t('core:tab_statistics')}
            />
          </Permission>

          <Permission component={EComponentTypes.PERFORMANCE_REVIEWS}>
            <NavigationBar.Item
              id="performance"
              path={[
                {
                  permission: [
                    EPermissions.ORGANISATION_PERFORMANCE_REVIEW_EVALUATIONS_VIEW_ALL,
                    EPermissions.NETWORK_PERFORMANCE_REVIEW_EVALUATIONS_VIEW_ALL
                  ],
                  path: networkUrl('/performance/evaluations')
                }
              ]}
              fallbackPath={networkUrl('/performance/my-evaluations')}
              icon="bar_chart"
              iconSize={20}
              tooltip={t('performance:tab_performance')}
            />
          </Permission>
          <NavigationBar.Divider />
          <Permission name={[
            EPermissions.NETWORK_NETWORKS_UPDATE,
            EPermissions.NETWORK_TEAMS_VIEW,
            EPermissions.NETWORK_USERS_UPDATE,
            EPermissions.NETWORK_NETWORK_ADMINS_VIEW,
            EPermissions.NETWORK_NETWORKS_UPDATE_COMPONENTS
          ]}
          >
            <NavigationBar.Item
              id="settings" path={networkUrl('/settings')}
              isActive={(match, l) => l.pathname.includes(networkUrl('/settings'))}
              icon="settings" tooltip={t('core:tab_settings')}
            />
          </Permission>
        </NavigationBar>

        <div id="Content">
          <TopBar
            loggedUser={loggedUser}
            organisation={organisation}
            networks={networks}
            network={network}
            setContentRef={this.setTopBarContentRef}
            onOpenProfile={(path) => history.push(`/profile/about${path || ''}`)}
            onLogout={this.handleLogout}
            onSelectNetwork={async (id) => {
              const path = await this.props.selectNetwork(id);

              AnalyticsService.register({ networkId: id });

              return path;
            }}
          >
            <NavLink
              to="/conversations" id="topbar_chat"
              className="TopBar__Trigger TopBar__Trigger--icon"
              activeClassName="TopBar__Trigger--open"
            >
              <Icon type="chat__filled" />
              {unread.chat ? <span className="TopBar__Trigger__Unread">{unread.chat}</span> : null}
            </NavLink>

            <Popup
              position="bottom right"
              on="click"
              style={{ width: 350, padding: 0 }}
              offset={[0, 4]}
              open={this.state.isNotificationOpen}
              onOpen={() => {
                this.toggleNotification(true);
                AnalyticsService.track(EEventNames.VIEWED_NOTIFICATIONS_LIST);
              }}
              onClose={() => this.toggleNotification(false)}
              content={(
                <Notifications onNotificationClick={this.handleNotificationClick} />
              )}
              trigger={(
                <div className="TopBar__Trigger TopBar__Trigger--icon" id="topbar_notifications">
                  <Icon type="notifications__filled" />
                  {
                    unread.notifications.total ?
                      (
                        <span className="TopBar__Trigger__Unread">{unread.notifications.total}</span>
                      ) :
                      null
                  }
                </div>
              )}
            />
          </TopBar>
          <div className="Content__Wrapper">
            {error && (
              <ErrorComponent error={error} />
            )}
            {!error && (
              <TopBarContentContext.Provider value={this.topBarContentRef}>
                <Switch>
                  <Route
                    path={[
                      '/networks/:networkId/messages/:type/:id',
                      '/networks/:networkId/messages/:type',
                      '/networks/:networkId/feed'
                    ]}
                    component={SocialContainer}
                  />
                  <PermissionRoute
                    path="/networks/:networkId/survey/:surveyId/:page"
                    component={SurveySubmissionEditor}
                    exact
                    permission={{ component: EComponentTypes.SURVEYS }}
                  />
                  <Route
                    path={[
                      '/networks/:networkId/users',
                      '/networks/:networkId/users/filter/:filter'
                    ]}
                    exact
                    component={EmployeesContainer}
                  />
                  <PermissionRoute
                    path="/networks/:networkId/forms"
                    component={NetworkForms}
                    permission={{ component: EComponentTypes.FORMS }}
                  />
                  <Route
                    path={[
                      '/networks/:networkId/users/:userId/edit',
                      '/networks/:networkId/users/:userId'
                    ]}
                    component={NetworkUserContainer}
                  />
                  <Redirect
                    from="/networks/:networkId/schedules"
                    to="/networks/:networkId/calendar"
                  />
                  <Redirect
                    from="/networks/:networkId/flexchange"
                    to="/networks/:networkId/calendar"
                  />
                  <PermissionRoute
                    path="/networks/:networkId/calendar/:week?"
                    permission={{ component: EComponentTypes.CALENDAR }}
                    component={CalendarContainer}
                  />
                  <PermissionRoute
                    path="/networks/:networkId/events"
                    component={EventsRouter}
                    permission={{ component: EComponentTypes.EVENTS }}
                  />
                  <PermissionRoute
                    path="/networks/:networkId/documents"
                    component={DocumentsContainer}
                    permission={{ component: EComponentTypes.DOCUMENTS }}
                  />
                  <PermissionRoute
                    path="/networks/:networkId/settings"
                    component={SettingsContainer}
                    permission={{
                      name: [
                        EPermissions.NETWORK_NETWORKS_UPDATE,
                        EPermissions.NETWORK_TEAMS_VIEW,
                        EPermissions.NETWORK_USERS_UPDATE,
                        EPermissions.NETWORK_NETWORK_ADMINS_VIEW,
                        EPermissions.NETWORK_NETWORKS_UPDATE_COMPONENTS
                      ]
                    }}
                  />
                  <PermissionRoute
                    path="/networks/:networkId/analytics"
                    component={AnalyticsContainer}
                    permission={{
                      name: [
                        EPermissions.NETWORK_USER_STATISTICS_VIEW,
                        EPermissions.NETWORK_COMMUNICATION_STATISTICS_VIEW,
                        EPermissions.NETWORK_EXCHANGE_STATISTICS_VIEW,
                        EPermissions.NETWORK_ONBOARDING_STATISTICS
                      ]
                    }}
                  />
                  {/* TODO: Add performance tab */}
                  <PermissionRoute
                    path="/networks/:networkId/performance"
                    component={PerformanceContainer}
                    permission={{ component: EComponentTypes.PERFORMANCE_REVIEWS }}
                  />
                  <PermissionRoute
                    path="/networks/:networkId/highfives"
                    component={HighfivesContainer}
                    permission={{
                      name: [
                        EPermissions.NETWORK_USERS_VIEW,
                      ]
                    }}
                  />
                  <Redirect exact from="/networks/:networkId" to="/networks/:networkId/feed" />
                  <Route path="/profile">
                    <ProfileContainer userId={loggedUser.id} type={UserProfileTypes.OWN} />
                  </Route>
                  {
                    showAcademy && (
                      <Route path="/networks/:networkId/academy">
                        <EmployeeAcademy
                          orgId={organisation.id}
                          networkId={network.id}
                          onboardingEnabled={onboardingEnabled}
                          elearningEnabled={elearningEnabled}
                        />
                      </Route>
                    )
                  }
                  <Route
                    path={[
                      '/conversations',
                      '/conversations/:conversationId',
                      '/conversations/filter/:filter/:conversationId',
                      '/conversations/filter/:filter',
                      '/conversations/users/:userId',
                      '/conversations/:conversationId/message/:messageId',
                      '/conversations/filter/:filter/:conversationId/message/:messageId',
                    ]}
                    exact
                    render={() => <ChatContainer basePath="/conversations" unread={unread.chat} />}
                  />
                  <Route path="/messages/:messageId" component={MessageContainer} />
                  <Redirect exact from="/" to={networkUrl('/feed')} />
                  <Route component={RouteNotFound} />
                </Switch>
              </TopBarContentContext.Provider>
            )}
          </div>
        </div>

        {showRecognitionModal && (
          <DialogWrapper
            show={showRecognitionModal}
            size="small"
            onHide={this.closeRecognitionModal}
            className="Recognition_Modal"
            title={t('core:notifications_received_high_five')}
            hideFooter
            content={
              <RecognitionCard
                loggedUser={loggedUser}
                recognition={recognition && recognition.meta && recognition.meta.recognition}
                network={network}
                user={recognition && recognition.user}
                enablePopovers={false}
                isNotifiedUser
              />
            }
          />
        )}

        <AccessRequestsModalRenderer />
      </div>
    );
  }

}

const mapStateToProps = (state) => ({
  organisation: organisationSelector.selected(state),
  networks: userSelector.networks(state),
  loggedUser: userSelector.selected(state),
  network: networkSelector.selected(state),
  unread: state.notifications,
  permissions: state.permissions,
  onboardingEnabled: isEmployeeOnboardingEnabled(state),
  elearningEnabled: organisationSelector.isElearningEnabled(state)
});

const mapDispatchToProps = {
  logout: require('../authentication/actions/logout').default,
  selectNetwork: require('./actions/select-network').default,
  clearNotifications: require('../core/actions/clear-notifications').default,
};

const NetworkTemplateWithContext = withTranslation()(connect(mapStateToProps, mapDispatchToProps)(NetworkTemplate));

const NetworkTemplateWrapper = (props) => {
  const { openModal } = useAccessRequestsContext();

  return <NetworkTemplateWithContext {...props} openAccessRequestsModal={openModal} />;
};

const NetworkTemplateWithProvider = (props) => {
  return (
    <AccessRequestsProvider>
      <NetworkTemplateWrapper {...props} />
    </AccessRequestsProvider>
  );
};

export default NetworkTemplateWithProvider;
